import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

export const BRAND_SECTIONS = [
  {
    id: 'basics2', title: 'Company Details', fields: [
      { key: 'trade', label: 'Trade', type: 'select', options: ['', 'HVAC', 'Plumbing', 'Electrical', 'Multiple Trades'] },
      { key: 'years_in_business', label: 'Years in business', type: 'text', ph: '15' },
      { key: 'license_number', label: 'License number', type: 'text', ph: 'AZ ROC #123456' },
      { key: 'website', label: 'Website URL', type: 'url', ph: 'https://yourcompany.com' },
      { key: 'tracking_phone', label: 'Tracking phone number (for ads)', type: 'text', ph: '(555) 987-6543', hint: 'A separate number used only in ads so calls can be tracked back to campaigns.' },
      { key: 'review_link', label: 'Review link (Google review URL)', type: 'url', ph: 'https://g.page/r/.../review' },
    ],
  },
  {
    id: 'service_area', title: 'Service Area', fields: [
      { key: 'service_area', label: 'Main service area', type: 'textarea', ph: 'Phoenix metro — Mesa, Tempe, Chandler, Gilbert' },
      { key: 'secondary_cities', label: 'Secondary cities', type: 'text', ph: 'Scottsdale, Glendale' },
      { key: 'county_region', label: 'County / region', type: 'text', ph: 'Maricopa County' },
      { key: 'excluded_service_areas', label: 'Areas you do NOT serve', type: 'text', ph: 'Anything past Casa Grande' },
      { key: 'cities_to_grow', label: 'Cities you want MORE work in', type: 'text', ph: 'Gilbert, Queen Creek' },
    ],
  },
  {
    id: 'services', title: 'Services & Pricing', fields: [
      { key: 'services_offered', label: 'Services you offer', type: 'textarea', ph: 'AC repair, AC replacement, furnace repair, duct cleaning, maintenance plans' },
      { key: 'average_job_value_by_service', label: 'Average job value by service', type: 'textarea', ph: 'AC repair: $450 · AC replacement: $8,500 · Tune-up: $89' },
      { key: 'diagnostic_fee', label: 'Diagnostic fee', type: 'text', ph: '$89' },
      { key: 'services_to_grow', label: 'Services you want MORE of', type: 'text', ph: 'System replacements, IAQ upgrades' },
      { key: 'services_to_avoid', label: 'Services you do NOT want', type: 'text', ph: 'Window units, mobile homes' },
    ],
  },
  {
    id: 'offers', title: 'Offers, Financing & Warranty', fields: [
      { key: 'financing_offer', label: 'Financing offer', type: 'text', ph: '$0 down, as low as $89/month with approved credit' },
      { key: 'warranty_language', label: 'Warranty wording (exactly as you back it)', type: 'textarea', ph: '2-year parts & labor warranty on all repairs' },
    ],
  },
  {
    id: 'trust', title: 'Trust & Credentials', fields: [
      { key: 'emergency_service_available', label: '24/7 emergency service', type: 'checkbox' },
      { key: 'after_hours_available', label: 'After-hours availability', type: 'checkbox' },
      { key: 'family_owned', label: 'Family-owned', type: 'checkbox' },
      { key: 'veteran_owned', label: 'Veteran-owned', type: 'checkbox' },
      { key: 'woman_owned', label: 'Woman-owned', type: 'checkbox' },
      { key: 'faith_friendly', label: 'Faith-friendly', type: 'checkbox' },
      { key: 'residential_specialist', label: 'Residential specialist', type: 'checkbox' },
      { key: 'commercial_specialist', label: 'Commercial specialist', type: 'checkbox' },
      { key: 'emergency_specialist', label: 'Emergency specialist', type: 'checkbox' },
    ],
  },
  {
    id: 'voice', title: 'Voice & Positioning', fields: [
      { key: 'brand_tone', label: 'Brand tone', type: 'select', options: ['', 'Friendly & local', 'Professional & premium', 'Straight-talking & no-nonsense', 'Family-owned & warm', 'Urgent & responsive'], hint: 'Used by "Rewrite for My Voice."' },
      { key: 'tone_to_avoid', label: 'Tone to avoid', type: 'text', ph: 'Pushy sales talk, corporate jargon' },
      { key: 'main_differentiator', label: 'What makes you different', type: 'textarea', ph: 'Same-day service, techs in marked trucks, upfront pricing before work starts' },
      { key: 'why_choose_us', label: 'Why customers choose you', type: 'textarea', ph: 'We show up when we say we will, and the price we quote is the price you pay.' },
      { key: 'customer_compliments', label: 'What customers compliment most', type: 'textarea', ph: '"The tech explained everything." "They cleaned up after the job."' },
    ],
  },
  {
    id: 'marketing', title: 'Marketing Setup', fields: [
      { key: 'google_business_profile_link', label: 'Google Business Profile link', type: 'url', ph: 'https://maps.google.com/...' },
      { key: 'facebook_page', label: 'Facebook page', type: 'url', ph: 'https://facebook.com/yourcompany' },
      { key: 'crm_or_booking_platform', label: 'CRM / booking platform', type: 'text', ph: 'ServiceTitan, Jobber, Housecall Pro…' },
      { key: 'call_tracking_number', label: 'Call tracking provider/number', type: 'text', ph: 'CallRail — (555) 111-2222' },
      { key: 'email_list_available', label: 'I have a customer email list', type: 'checkbox' },
      { key: 'csr_available', label: 'I have a CSR / someone answering phones', type: 'checkbox' },
      { key: 'sms_permission_status', label: 'Can you legally text your customers?', type: 'select', options: ['', 'Yes — they opted in', 'Some of them', 'No / not sure'] },
    ],
  },
];

const inputCls = 'w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-construction-orange';

const Field = ({ field, value, onChange }) => {
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-center gap-3 p-3 rounded-lg bg-slate-blue border border-divider cursor-pointer hover:border-lime/40" data-testid={`brand-field-${field.key}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(field.key, e.target.checked)} className="w-4 h-4 accent-[#B7F04C]" />
        <span className="text-sm text-light-gray">{field.label}</span>
      </label>
    );
  }
  return (
    <div data-testid={`brand-field-${field.key}`}>
      <label className="block text-sm text-light-gray mb-1.5">{field.label}</label>
      {field.type === 'textarea' ? (
        <textarea value={value || ''} onChange={(e) => onChange(field.key, e.target.value)} placeholder={field.ph} rows={2} className={inputCls} />
      ) : field.type === 'select' ? (
        <select value={value || ''} onChange={(e) => onChange(field.key, e.target.value)} className={inputCls}>
          {field.options.map((o) => <option key={o} value={o}>{o || 'Choose…'}</option>)}
        </select>
      ) : (
        <input type={field.type} value={value || ''} onChange={(e) => onChange(field.key, e.target.value)} placeholder={field.ph} className={inputCls} />
      )}
      {field.hint && <p className="text-xs text-muted mt-1">{field.hint}</p>}
    </div>
  );
};

export const BrandProfileSections = ({ profile, onChange }) => {
  const [open, setOpen] = useState({ basics2: true });
  return (
    <div className="space-y-4 mt-8">
      <div>
        <h2 className="text-2xl font-bold text-white">Full Brand Profile</h2>
        <p className="text-sm text-gray mt-1">The more you fill in, the sharper your AI-personalized campaigns get. Everything is optional — start with what you know.</p>
      </div>
      {BRAND_SECTIONS.map((section) => {
        const filled = section.fields.filter((f) => {
          const v = profile[f.key];
          return f.type === 'checkbox' ? v === true : (v || '').toString().trim();
        }).length;
        const isOpen = !!open[section.id];
        return (
          <div key={section.id} className="border border-divider rounded-xl overflow-hidden" data-testid={`brand-section-${section.id}`}>
            <button
              onClick={() => setOpen((p) => ({ ...p, [section.id]: !isOpen }))}
              data-testid={`brand-section-toggle-${section.id}`}
              className="w-full flex items-center justify-between px-5 py-4 bg-black hover:bg-card-bg transition-colors"
            >
              <span className="text-white font-bold flex items-center gap-2">
                {isOpen ? <ChevronDown size={16} className="text-lime" /> : <ChevronRight size={16} className="text-gray" />}
                {section.title}
              </span>
              <span className={`text-xs font-semibold ${filled > 0 ? 'text-lime' : 'text-gray'}`}>{filled}/{section.fields.length} filled</span>
            </button>
            {isOpen && (
              <div className={`p-5 grid gap-4 ${section.fields.every((f) => f.type === 'checkbox') ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
                {section.fields.map((f) => (
                  <Field key={f.key} field={f} value={profile[f.key]} onChange={onChange} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
